import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import DashboardTopbar from "components/DashboardTopbar";
import DashboardMenuBar from "components/DashboardMenuBar";
import { useAppContext } from "components/Context/AppContext";
import "styles/dashboard.css";
import "./settings.css";

const DASHBOARD_API = process.env.REACT_APP_API_URL || "http://localhost:5050/api";

// ── Parameter row order for display ──────────────────────────────────────────
const PARAM_ROWS = [
  { key: "risk_horizon", label: "Risk Horizon"    },
  { key: "risk_measure", label: "Risk Measure"    },
  { key: "benchmark",    label: "Benchmark"       },
  { key: "base_currency",label: "Base Currency"   },
  { key: "beta_key",     label: "Beta Index"      },
  { key: "exp_return",   label: "Expected Return" },
];

// ── Allocation & VaR contribution rows (Card 4) ───────────────────────────────
const ALLOC_ROWS = [
  { allocKey: "alloc_equity_pct",      varKey: "var_alloc_equity_pct",      label: "Equity"       },
  { allocKey: "alloc_fixedincome_pct", varKey: "var_alloc_fixedincome_pct", label: "Fixed Income" },
  { allocKey: "alloc_alternative_pct", varKey: "var_alloc_alternative_pct", label: "Alternative"  },
  { allocKey: "alloc_multiasset_pct",  varKey: "var_alloc_multiasset_pct",  label: "Multi-Asset"  },
  { allocKey: "alloc_cash_pct",        varKey: "var_alloc_cash_pct",        label: "Cash"         },
];

// ── Concentration limit rows ──────────────────────────────────────────────────
const CONC_ROWS = [
  { allocKey: "con_limit_asset_pct",    varKey: "var_con_asset_pct",    label: "Asset Class"  },
  { allocKey: "con_limit_region_pct",   varKey: "var_con_region_pct",   label: "Region"       },
  { allocKey: "con_limit_currency_pct", varKey: "var_con_currency_pct", label: "Currency"     },
  { allocKey: "con_limit_industry_pct", varKey: "var_con_industry_pct", label: "Industry"     },
  { allocKey: "con_limit_name_pct",     varKey: "var_con_name_pct",     label: "Single Name"  },
];

// ── Risk measure limit rows ───────────────────────────────────────────────────
const RISK_ROWS = [
  { key: "var_limit_dollar", label: "VaR Limit ($)",    unit: "$m", min: 0, max: undefined, step: 0.1 },
  { key: "var_limit_pct",    label: "VaR Limit (%)",    unit: "%",  min: 0, max: 100,       step: 0.5 },
  { key: "vol_limit_pct",    label: "Volatility Limit", unit: "%",  min: 0, max: 100,       step: 0.5 },
];

// ── Risk Level presets ────────────────────────────────────────────────────────
const RL_LEVELS = [
  { key: "conservative", label: "Conservative", desc: "Tight limits"   },
  { key: "moderate",     label: "Moderate",     desc: "Balanced"       },
  { key: "aggressive",   label: "Aggressive",   desc: "Loose limits"   },
  { key: "custom",       label: "Custom",       desc: "User-defined"   },
];

const RL_KEY = "trg_risk_level";

// ── Sub-components ─────────────────────────────────────────────────────────────
function Sel({ value, options, onChange, disabled }) {
  const safeVal = value ?? "";
  return (
    <select
      className="sett-select"
      value={safeVal}
      disabled={disabled}
      onChange={e => onChange(e.target.value)}
    >
      {safeVal === "" && <option value="" disabled>—</option>}
      {(options || []).map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function NumInput({ value, min, max, step, unit, onChange }) {
  const display = value == null ? "" : value;
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
      <input
        className="sett-input"
        type="number"
        value={display}
        min={min}
        max={max}
        step={step}
        placeholder="—"
        onChange={e => onChange(e.target.value === "" ? null : e.target.value)}
      />
      {unit && <span className="sett-unit">{unit}</span>}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function SettingsPage() {
  const { selectedAccount } = useAppContext();
  const [theme, setTheme]   = useState(() => localStorage.getItem("trg_theme") || "dark");

  // Risk Level preset selector (localStorage) + presets from API
  const [riskLevel,      setRiskLevel]      = useState(() => localStorage.getItem(RL_KEY) || "custom");
  const [presets,        setPresets]        = useState({});
  const [presetsLoading, setPresetsLoading] = useState(true);
  const [presetsError,   setPresetsError]   = useState(false);

  // Card 1: Parameters (API)
  const [paramValues,      setParamValues]      = useState({});
  const [savedParamValues, setSavedParamValues] = useState({});
  const [paramOptions,     setParamOptions]     = useState({});
  const [paramLoading,     setParamLoading]     = useState(true);
  const [paramError,       setParamError]       = useState(null);

  // Cards 2–4: Limits (API)
  // limitValues shape: { concentration: {...}, risk: {...}, alloc: {...} }
  const [limitValues,      setLimitValues]      = useState({ concentration: {}, risk: {}, alloc: {} });
  const [savedLimitValues, setSavedLimitValues] = useState({ concentration: {}, risk: {}, alloc: {} });
  const [limitLoading,     setLimitLoading]     = useState(true);
  const [limitError,       setLimitError]       = useState(null);

  const [toast, setToast] = useState(null); // "saved" | "error"

  // ── Fetch parameters ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedAccount?.account_id) return;
    const token = sessionStorage.getItem("token");
    if (!token) return;
    setParamLoading(true);
    setParamError(null);
    fetch(`${DASHBOARD_API}/settings/parameters?account_id=${selectedAccount.account_id}&token=${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) throw new Error(data.error);
        const vals = data.values || {};
        setParamValues(vals);
        setSavedParamValues(vals);
        setParamOptions(data.options || {});
        if (vals.risk_level) {
          setRiskLevel(vals.risk_level);
          localStorage.setItem(RL_KEY, vals.risk_level);
        }
      })
      .catch(e => setParamError(e.message))
      .finally(() => setParamLoading(false));
  }, [selectedAccount?.account_id]);

  // ── Fetch limits ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedAccount?.account_id) return;
    const token = sessionStorage.getItem("token");
    if (!token) return;
    setLimitLoading(true);
    setLimitError(null);
    fetch(`${DASHBOARD_API}/settings/limits?account_id=${selectedAccount.account_id}&token=${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) throw new Error(data.error);
        // Convert var_limit_dollar from raw $ to $m for display
        const risk = { ...(data.risk || {}) };
        if (risk.var_limit_dollar != null) {
          risk.var_limit_dollar = +(risk.var_limit_dollar / 1_000_000).toFixed(4);
        }
        const vals = { concentration: data.concentration || {}, risk, alloc: data.alloc || {} };
        setLimitValues(vals);
        setSavedLimitValues(vals);
      })
      .catch(e => setLimitError(e.message))
      .finally(() => setLimitLoading(false));
  }, [selectedAccount?.account_id]);

  // ── Fetch presets ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!selectedAccount?.account_id) return;
    const token = sessionStorage.getItem("token");
    if (!token) { setPresetsLoading(false); return; }
    fetch(`${DASHBOARD_API}/settings/presets?account_id=${selectedAccount.account_id}&token=${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) throw new Error(data.error);
        setPresets(data);
      })
      .catch(() => setPresetsError(true))
      .finally(() => setPresetsLoading(false));
  }, [selectedAccount?.account_id]);

  // ── Setters ─────────────────────────────────────────────────────────────────
  const setParam = (key, val) => setParamValues(v => ({ ...v, [key]: val }));

  const setAlloc = (key, val) => {
    if (riskLevel !== "custom") { setRiskLevel("custom"); localStorage.setItem(RL_KEY, "custom"); }
    setLimitValues(v => ({ ...v, alloc: { ...v.alloc, [key]: val } }));
  };

  const setConc = (key, val) => {
    if (riskLevel !== "custom") { setRiskLevel("custom"); localStorage.setItem(RL_KEY, "custom"); }
    setLimitValues(v => ({ ...v, concentration: { ...v.concentration, [key]: val } }));
  };

  const setRisk = (key, val) => {
    if (riskLevel !== "custom") { setRiskLevel("custom"); localStorage.setItem(RL_KEY, "custom"); }
    setLimitValues(v => ({ ...v, risk: { ...v.risk, [key]: val } }));
  };

  const applyPreset = (level) => {
    localStorage.setItem(RL_KEY, level);
    setRiskLevel(level);
    if (level === "custom") return;
    const p = presets[level];
    if (!p) return;
    setLimitValues(v => ({
      concentration: { ...v.concentration, ...p.concentration },
      risk:          { ...v.risk,          ...p.risk          },
      alloc:         { ...v.alloc,         ...p.alloc         },
    }));
  };

  // ── Dirty detection ─────────────────────────────────────────────────────────
  const dirtyParams = PARAM_ROWS
    .map(r => r.key)
    .filter(k => paramValues[k] !== savedParamValues[k]);

  const dirtyConcKeys = CONC_ROWS
    .flatMap(r => [r.allocKey, r.varKey])
    .filter(k => String(limitValues.concentration[k] ?? "") !== String(savedLimitValues.concentration[k] ?? ""));

  const dirtyRiskKeys = RISK_ROWS
    .map(r => r.key)
    .filter(k => String(limitValues.risk[k] ?? "") !== String(savedLimitValues.risk[k] ?? ""));

  const dirtyAllocKeys = ALLOC_ROWS
    .flatMap(r => [r.allocKey, r.varKey])
    .filter(k => String(limitValues.alloc[k] ?? "") !== String(savedLimitValues.alloc[k] ?? ""));

  const dirtyCount = dirtyParams.length + dirtyConcKeys.length + dirtyRiskKeys.length + dirtyAllocKeys.length;

  // ── Save ────────────────────────────────────────────────────────────────────
  const saveSettings = async () => {
    const token     = sessionStorage.getItem("token");
    const accountId = selectedAccount?.account_id;

    if (!token || !accountId) {
      setSavedParamValues({ ...paramValues });
      setSavedLimitValues(limitValues);
      showToast("saved");
      return;
    }

    try {
      // Card 1: parameters
      const paramRes = await fetch(
        `${DASHBOARD_API}/settings/parameters?account_id=${accountId}&token=${token}`,
        {
          method:  "PUT",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({ ...paramValues, risk_level: riskLevel }),
        }
      );
      const paramData = await paramRes.json();
      if (!paramRes.ok || paramData.error) throw new Error(paramData.error || "Save failed");

      // Cards 2–3: limits — convert var_limit_dollar back to raw $ before sending
      const riskForApi = { ...limitValues.risk };
      if (riskForApi.var_limit_dollar != null) {
        riskForApi.var_limit_dollar = riskForApi.var_limit_dollar * 1_000_000;
      }
      const limitsBody = {
        concentration: limitValues.concentration,
        risk:          riskForApi,
        alloc:         limitValues.alloc,
      };
      const limitsRes = await fetch(
        `${DASHBOARD_API}/settings/limits?account_id=${accountId}&token=${token}`,
        {
          method:  "PUT",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify(limitsBody),
        }
      );
      const limitsData = await limitsRes.json();
      if (!limitsRes.ok || limitsData.error) throw new Error(limitsData.error || "Save failed");

      setSavedParamValues({ ...paramValues });
      setSavedLimitValues(limitValues);
      showToast("saved");
    } catch {
      showToast("error");
    }
  };

  const showToast = (type) => {
    setToast(type);
    setTimeout(() => setToast(null), 3000);
  };

  return (
    <div className={`trg-shell${theme === "light" ? " theme-light" : ""}`}>
      <Helmet><title>Settings — Tail Risk Global</title></Helmet>
      <DashboardTopbar />
      <DashboardMenuBar onThemeChange={setTheme} />

      <div className="sett-body">
        <div className="sett-page-hdr">
          <div>
            <div className="sett-page-title">Settings</div>
            <div className="sett-page-sub">User-defined parameters and limits — applied across all views</div>
          </div>
          <button className="sett-save-btn" onClick={saveSettings}>
            Save
            {dirtyCount > 0 && (
              <span className="sett-dirty-badge">{dirtyCount}</span>
            )}
          </button>
        </div>

        {/* ── Risk Level preset selector ── */}
        <div className="trg-card sett-rl-card">
          <div className="sett-rl-hdr">
            <div>
              <div className="trg-card-title">Risk Level</div>
              <div className="sett-card-sub">Choose a preset for concentration, risk-measure, and allocation limits — or pick Custom to define your own.</div>
            </div>
            <div className="sett-rl-segs">
              {RL_LEVELS.map(({ key, label, desc }) => {
                const disabled = key !== "custom" && (presetsLoading || presetsError);
                return (
                  <button
                    key={key}
                    className={`sett-rl-seg${riskLevel === key ? " active" : ""}${disabled ? " disabled" : ""}`}
                    onClick={() => !disabled && applyPreset(key)}
                    title={presetsError && key !== "custom" ? "Could not load presets" : undefined}
                  >
                    <div className="sett-rl-seg-name">{label}</div>
                    <div className="sett-rl-seg-desc">
                      {presetsLoading && key !== "custom" ? "Loading…" : desc}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="sett-grid">

          {/* ── Card 1: Parameters (API) ── */}
          <div className="trg-card">
            <div className="trg-card-header">
              <div className="trg-card-title">Parameters</div>
              <div className="sett-card-sub">Account risk and reporting defaults</div>
            </div>
            <div style={{ overflowX: "auto" }}>
              {paramError ? (
                <div className="sett-param-error">Could not load parameters: {paramError}</div>
              ) : (
                <table className="sett-table">
                  <thead>
                    <tr><th>Parameter</th><th className="center">Value</th></tr>
                  </thead>
                  <tbody>
                    {PARAM_ROWS.map(({ key, label }) => (
                      <tr key={key} className={dirtyParams.includes(key) ? "sett-row-dirty" : ""}>
                        <td>{label}</td>
                        <td className="center">
                          <Sel
                            value={paramValues[key]}
                            options={paramOptions[key]}
                            disabled={paramLoading}
                            onChange={v => setParam(key, v)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* ── Card 2: Concentration Limits (API) ── */}
          <div className="trg-card">
            <div className="trg-card-header">
              <div className="trg-card-title">Concentration Limits</div>
              <div className="sett-card-sub">Max % per category</div>
            </div>
            <div style={{ overflowX: "auto" }}>
              {limitError ? (
                <div className="sett-param-error">Could not load limits: {limitError}</div>
              ) : (
                <table className="sett-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th className="center">Allocation Limit</th>
                      <th className="center">VaR Limit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CONC_ROWS.map(({ allocKey, varKey, label }) => (
                      <tr key={allocKey} className={
                        dirtyConcKeys.includes(allocKey) || dirtyConcKeys.includes(varKey)
                          ? "sett-row-dirty" : ""
                      }>
                        <td>{label}</td>
                        <td className="center">
                          <NumInput
                            value={limitLoading ? "" : limitValues.concentration[allocKey]}
                            min={0} max={100} step={1} unit="%"
                            onChange={v => setConc(allocKey, v)}
                          />
                        </td>
                        <td className="center">
                          <NumInput
                            value={limitLoading ? "" : limitValues.concentration[varKey]}
                            min={0} max={100} step={1} unit="%"
                            onChange={v => setConc(varKey, v)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* ── Card 3: Risk Measure Limits (API) ── */}
          <div className="trg-card">
            <div className="trg-card-header">
              <div className="trg-card-title">Risk Measure Limits</div>
              <div className="sett-card-sub">Used in gauge charts and limit lines across all views</div>
            </div>
            <div style={{ overflowX: "auto" }}>
              {limitError ? (
                <div className="sett-param-error">Could not load limits: {limitError}</div>
              ) : (
                <table className="sett-table">
                  <thead>
                    <tr><th>Parameter</th><th className="center">Value</th></tr>
                  </thead>
                  <tbody>
                    {RISK_ROWS.map(({ key, label, unit, min, max, step }) => (
                      <tr key={key} className={dirtyRiskKeys.includes(key) ? "sett-row-dirty" : ""}>
                        <td>{label}</td>
                        <td className="center">
                          <NumInput
                            value={limitLoading ? "" : limitValues.risk[key]}
                            min={min} max={max} step={step} unit={unit}
                            onChange={v => setRisk(key, v)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* ── Card 4: Allocation & VaR Contribution Limits (API) ── */}
          <div className="trg-card">
            <div className="trg-card-header">
              <div className="trg-card-title">Allocation and Risk Contribution Limits</div>
              <div className="sett-card-sub">Maximum % per category</div>
            </div>
            <div style={{ overflowX: "auto" }}>
              {limitError ? (
                <div className="sett-param-error">Could not load limits: {limitError}</div>
              ) : (
                <table className="sett-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th className="center">Allocation</th>
                      <th className="center">VaR Contribution</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ALLOC_ROWS.map(({ allocKey, varKey, label }) => (
                      <tr key={allocKey} className={
                        dirtyAllocKeys.includes(allocKey) || dirtyAllocKeys.includes(varKey)
                          ? "sett-row-dirty" : ""
                      }>
                        <td>{label}</td>
                        <td className="center">
                          <NumInput
                            value={limitLoading ? "" : limitValues.alloc[allocKey]}
                            min={0} max={100} step={1} unit="%"
                            onChange={v => setAlloc(allocKey, v)}
                          />
                        </td>
                        <td className="center">
                          <NumInput
                            value={limitLoading ? "" : limitValues.alloc[varKey]}
                            min={0} max={100} step={1} unit="%"
                            onChange={v => setAlloc(varKey, v)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

        </div>
      </div>

      {toast === "saved" && <div className="sett-toast">✓ Settings saved</div>}
      {toast === "error" && <div className="sett-toast sett-toast-error">✕ Save failed — check connection</div>}
    </div>
  );
}
